import { createContext, useContext, useState, useCallback } from "react";
import { TargetsLink } from "./TargetsLink";
import { useYear } from "../../../context/YearContext";
import { currentDateTime } from "../../../utils/dateUtils.js";
import {
  PRELIM_DATES,
  matchesStage,
  stageOf,
} from "../../../utils/archeryConfig.js";

const TargetsContext = createContext();

export const TargetsProvider = ({ children }) => {
  const { year } = useYear();
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedStage, setSelectedStage] = useState("PRE");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // date(YYYY-MM-DD): 고른 날짜. 없으면 오늘 기준으로 서버가 고른 경기일
  // (서버는 그 날짜에 경기가 없으면 가까운 경기일을 주므로, 선택한 연도·날짜의 경기만 남김)
  // stage: 경기 단계 (PRE 예선, 16/8/4/3/2 본선). 본선은 그날 다른 단계와 함께 오므로 고른 단계 조만 남김
  //        안 고르면 그날 다가오는(지났으면 마지막) 경기의 단계 (예: 11/3 아침 → 16강, 예선 날 → 예선)
  const fetchRounds = useCallback(
    async (distance, date, stage) => {
      setLoading(true);
      setError(null);
      try {
        const current = currentDateTime();
        const datetime = date
          ? `${date} 00:00:00`
          : current.startsWith(year)
          ? current
          : `${PRELIM_DATES[year]?.[0]} 00:00:00`;

        const link = new TargetsLink();
        await link.request(datetime, distance, year);

        let slots = link.timeSlots.filter((timeSlot) =>
          timeSlot.round_schedule?.round_time?.startsWith(year)
        );
        const slotDate = slots[0]?.round_schedule.round_time.slice(0, 10);
        const targetDate = date || slotDate || PRELIM_DATES[year]?.[0];
        slots = slots.filter((timeSlot) =>
          timeSlot.round_schedule.round_time.startsWith(targetDate)
        );

        // 시간순 정렬
        slots.sort((a, b) => {
          const timeA = new Date(a.round_schedule?.round_time);
          const timeB = new Date(b.round_schedule?.round_time);
          return timeA - timeB;
        });

        const upcoming =
          slots.find(({ round_schedule }) => round_schedule.round_time >= current) ||
          slots[slots.length - 1];
        const targetStage = stage || stageOf(upcoming?.rounds[0]?.joType);
        slots = slots
          .map((timeSlot) => ({
            ...timeSlot,
            rounds: timeSlot.rounds.filter(({ joType }) =>
              matchesStage(joType, targetStage, distance)
            ),
          }))
          .filter((timeSlot) => timeSlot.rounds.length > 0);

        setSelectedDate(targetDate);
        setSelectedStage(targetStage);
        setTimeSlots(slots);
      } catch (error) {
        console.error("Failed to fetch rounds:", error);
        setError(error.message);
        setTimeSlots([]);
      } finally {
        setLoading(false);
      }
    },
    [year]
  );

  // 드롭다운 날짜: 예선 날짜 + (본선 기간 등) 현재 보고 있는 날짜
  const dates = [...(PRELIM_DATES[year] || [])];
  if (selectedDate && !dates.includes(selectedDate)) dates.push(selectedDate);

  const value = {
    timeSlots,
    dates,
    selectedDate,
    selectedStage,
    loading,
    error,
    fetchRounds,
  };

  return (
    <TargetsContext.Provider value={value}>{children}</TargetsContext.Provider>
  );
};

export const useTargets = () => {
  const context = useContext(TargetsContext);
  if (!context) {
    throw new Error("useTargets must be used within a TargetsProvider");
  }
  return context;
};
